import { useEffect, useRef, useState } from "react";
// Styles d'origine de la fenetre « Choisis ta ville », partages tels quels.
import styles from "./LocationPrompt.module.css";

// Selecteur de ville partage : fenetre « Choisis ta ville » et « Ma ville »
// dans l'edition du profil. Meme liste, meme recherche, meme clavier.

const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const AUTOCOMPLETE_MIN_LENGTH = 2;
export const GPS_OPTION_ID = "gps";

// Ville proposee par defaut, puis villes voisines affichees avant toute saisie.
// coordinates = [lng, lat], meme format que forwardGeocodeAutocomplete.
export const DEFAULT_CITY = Object.freeze({ city: "Lyon", label: "Lyon, Rhône", sublabel: "Rhône", coordinates: [4.8357, 45.764] });
export const NEARBY_CITIES = Object.freeze([
  DEFAULT_CITY,
  { city: "Villeurbanne", label: "Villeurbanne, Rhône", sublabel: "Rhône", coordinates: [4.88, 45.771] },
  { city: "Vénissieux", label: "Vénissieux, Rhône", sublabel: "Rhône", coordinates: [4.8867, 45.6975] },
  { city: "Caluire-et-Cuire", label: "Caluire-et-Cuire, Rhône", sublabel: "Rhône", coordinates: [4.8467, 45.7953] },
  { city: "Bron", label: "Bron, Rhône", sublabel: "Rhône", coordinates: [4.9131, 45.7386] },
  { city: "Vaulx-en-Velin", label: "Vaulx-en-Velin, Rhône", sublabel: "Rhône", coordinates: [4.9219, 45.7786] },
]);

export function cityName(suggestion) {
  return suggestion?.city || suggestion?.sublabel?.split(",")[0]?.trim() || suggestion?.label || "";
}

export function cityKey(suggestion) {
  return `${suggestion.label}-${suggestion.coordinates?.join(",")}`;
}

function optionKey(option) {
  return option.id === GPS_OPTION_ID ? GPS_OPTION_ID : cityKey(option);
}

// Met en gras la partie tapee par le membre.
function Highlighted({ text, query }) {
  const needle = query.trim().toLocaleLowerCase("fr");
  const index = needle ? text.toLocaleLowerCase("fr").indexOf(needle) : -1;
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.match}>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
}

export function PinIcon({ className, done = false, size = 34 }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width={size} height={size} focusable="false" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21.2s-6.6-5.7-6.6-11.2a6.6 6.6 0 0 1 13.2 0c0 5.5-6.6 11.2-6.6 11.2Z" />
      {done ? <path d="m9.3 10.1 1.9 1.9 3.6-3.7" /> : <circle cx="12" cy="10" r="2.3" />}
    </svg>
  );
}

// Champ « Ta ville » facon liste deroulante.
export function CityTrigger({ city, onClick, disabled = false, triggerRef = null }) {
  const name = cityName(city);
  return (
    <button
      ref={triggerRef}
      type="button"
      className={styles.trigger}
      onClick={onClick}
      aria-haspopup="listbox"
      aria-expanded={false}
      aria-label={`Ta ville : ${name}. Changer`}
      disabled={disabled}
    >
      <span className={styles.triggerPin}><PinIcon size={18} /></span>
      <span className={styles.triggerText}>
        <strong>{name}</strong>
        {city?.sublabel ? <span>{city.sublabel}</span> : null}
      </span>
      <span className={styles.triggerAction}>Changer</span>
      <svg className={styles.chevron} viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
        <path d="m7.5 5.5 4.5 4.5-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

// Ecran de recherche : champ en haut, ligne d'etat de hauteur reservee, seule
// la liste defile. Le parent fournit un conteneur en colonne de hauteur fixe
// (scrollContainerRef) et gere Echap.
export function CitySearchPanel({
  idPrefix,
  titleId,
  title = "Choisis ta ville",
  selectedCity,
  gpsAvailable = false,
  searchCities,
  onPickCity,
  onUseGps,
  onBack,
  scrollContainerRef = null,
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Option active de la liste, reperee par sa cle (jamais par sa position, qui
  // change avec les resultats). null = aucun choix explicite.
  const [activeKey, setActiveKey] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const searchSeqRef = useRef(0);
  // Une reponse arrivee apres la fermeture n'agit plus.
  const aliveRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    inputRef.current?.focus({ preventScroll: true });
    return () => {
      aliveRef.current = false;
      window.clearTimeout(debounceRef.current);
    };
  }, []);

  const hasQuery = query.trim().length >= AUTOCOMPLETE_MIN_LENGTH;
  const cityOptions = hasQuery ? suggestions : NEARBY_CITIES;
  const options = [...(gpsAvailable ? [{ id: GPS_OPTION_ID }] : []), ...cityOptions];
  // Regle de l'option active :
  // - un choix explicite (fleches, souris) est garde tant que l'option est affichee ;
  // - sinon, la premiere ville affichee ;
  // - aucune ville (chargement, aucun resultat, erreur) : aucune option active.
  // Le GPS n'est donc jamais actif sans choix explicite : Entree apres une
  // saisie ne lance jamais la localisation.
  const explicitIndex = activeKey ? options.findIndex((option) => optionKey(option) === activeKey) : -1;
  const activeIdx = explicitIndex >= 0 ? explicitIndex : options.findIndex((option) => option.id !== GPS_OPTION_ID);

  // L'option active reste visible : seule la liste defile, ni la fenetre ni la
  // page. Le focus reste dans le champ (combobox).
  useEffect(() => {
    const list = listRef.current;
    const active = activeIdx >= 0 ? list?.children[activeIdx] : null;
    if (!active) return;
    const top = active.offsetTop;
    const bottom = top + active.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    // Ecran bas (clavier mobile ouvert) : le conteneur defile a son tour pour
    // que l'option reste a l'ecran. La page, elle, ne bouge pas.
    const container = scrollContainerRef?.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    if (activeRect.bottom > containerRect.bottom) container.scrollTop += activeRect.bottom - containerRect.bottom + 8;
    else if (activeRect.top < containerRect.top) container.scrollTop -= containerRect.top - activeRect.top + 8;
  }, [activeIdx, options.length, scrollContainerRef]);

  // Survol : seul un vrai deplacement de la souris change l'option active. Une
  // liste qui defile sous une souris immobile ne vole pas la selection clavier.
  const pointerRef = useRef(null);
  const handleOptionPointerMove = (event, option) => {
    const last = pointerRef.current;
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (last && last.x === event.clientX && last.y === event.clientY) return;
    setActiveKey(optionKey(option));
  };

  const handlePickOption = (option) => {
    if (option.id === GPS_OPTION_ID) {
      onUseGps?.();
      return;
    }
    const [lng, lat] = option.coordinates || [];
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    onPickCity(option);
  };

  const handleQueryChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setSuggestions([]);
    // Nouvelle saisie : retour a la regle par defaut (premiere ville affichee).
    setActiveKey(null);
    window.clearTimeout(debounceRef.current);
    // Toute frappe invalide les reponses encore attendues.
    const seq = searchSeqRef.current + 1;
    searchSeqRef.current = seq;
    const trimmed = value.trim();
    if (trimmed.length < AUTOCOMPLETE_MIN_LENGTH) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      let results = [];
      try {
        results = await searchCities(trimmed);
      } catch {
        // Recherche indisponible : la liste reste vide avec son message.
      }
      // Une frappe plus recente l'emporte sur une reponse en retard.
      if (!aliveRef.current || searchSeqRef.current !== seq) return;
      setSuggestions(Array.isArray(results) ? results : []);
      setIsSearching(false);
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  };

  const handleQueryKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!options.length) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = activeIdx < 0
        ? (step > 0 ? 0 : options.length - 1)
        : (activeIdx + step + options.length) % options.length;
      setActiveKey(optionKey(options[next]));
    } else if (event.key === "Enter") {
      event.preventDefault();
      // Aucune option active : Entree ne fait rien (jamais de GPS implicite).
      if (activeIdx >= 0) handlePickOption(options[activeIdx]);
    }
  };

  const activeOption = activeIdx >= 0 ? options[activeIdx] : null;
  const listboxId = `${idPrefix}-listbox`;
  const optionDomId = (option) => `${idPrefix}-option-${option.id === GPS_OPTION_ID ? "gps" : cityKey(option).replace(/[^a-z0-9]/gi, "")}`;
  const searchStatus = isSearching
    ? "Recherche…"
    : hasQuery && suggestions.length === 0
      ? "Aucune ville trouvée."
      : hasQuery ? "" : "Ta ville n’y est pas ? Tape son nom.";

  return (
    <>
      <div className={styles.searchHeader}>
        <button type="button" className={styles.back} onClick={onBack} aria-label="Retour">
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">
            <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h2 id={titleId} className={styles.searchTitle}>{title}</h2>
      </div>
      <div className={styles.searchRow}>
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
          <circle cx="9" cy="9" r="5.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
          <path d="m13.2 13.2 3.6 3.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          className={styles.searchInput}
          type="text"
          inputMode="search"
          autoComplete="off"
          placeholder="Rechercher une ville…"
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleQueryKeyDown}
          role="combobox"
          aria-label="Rechercher une ville"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOption ? optionDomId(activeOption) : undefined}
        />
      </div>
      <div className={styles.listStatus} aria-live="polite">{searchStatus}</div>
      <ul ref={listRef} className={styles.list} id={listboxId} role="listbox" aria-label="Villes">
        {options.map((option, index) => {
          const isGps = option.id === GPS_OPTION_ID;
          const isSelected = !isGps && Boolean(selectedCity) && cityKey(option) === cityKey(selectedCity);
          return (
            <li
              key={isGps ? GPS_OPTION_ID : cityKey(option)}
              id={optionDomId(option)}
              role="option"
              aria-selected={isSelected}
              className={[
                styles.option,
                isGps ? styles.optionGps : "",
                index === activeIdx ? styles.optionActive : "",
              ].join(" ")}
              onMouseMove={(event) => handleOptionPointerMove(event, option)}
              // mousedown : garde le focus dans le champ jusqu'au choix.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handlePickOption(option)}
            >
              <span className={styles.optionIcon} aria-hidden="true">
                {isGps ? (
                  <svg viewBox="0 0 20 20" width="18" height="18" focusable="false">
                    <circle cx="10" cy="10" r="3" fill="currentColor" />
                    <circle cx="10" cy="10" r="6.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M10 1.5v2.4M10 16.1v2.4M1.5 10h2.4M16.1 10h2.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                ) : <PinIcon size={18} />}
              </span>
              <span className={styles.optionText}>
                {isGps ? (
                  <>
                    <strong>Utiliser ma position actuelle</strong>
                    <span>Trouvée par ton téléphone</span>
                  </>
                ) : (
                  <>
                    <strong><Highlighted text={cityName(option)} query={hasQuery ? query : ""} /></strong>
                    {option.sublabel ? <span>{option.sublabel}</span> : null}
                  </>
                )}
              </span>
              {isSelected ? (
                <svg className={styles.check} viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
                  <path d="m5 10.4 3.2 3.1L15 6.6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}
